import { FormEvent, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FullPageLoader } from '@/components/common/FullPageLoader';
import {
  useConsultaPublicaPublic,
  useEnviarManifestacao,
} from '@/hooks/useConsultasPublicas';

import './ConsultaPublicaPublicPage.css';

function formatDate(value?: string | null) {
  if (!value) return '—';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
}

export function ConsultaPublicaPublicPage() {
  const { token } = useParams<{ token: string }>();
  const { data: consulta, isLoading, error } = useConsultaPublicaPublic(token);
  const enviarManifestacao = useEnviarManifestacao();

  const [pagina, setPagina] = useState('');
  const [comentario, setComentario] = useState('');
  const [votos, setVotos] = useState<string[]>([]);
  const [nome, setNome] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [email, setEmail] = useState('');
  const [areaAtuacaoProfissional, setAreaAtuacaoProfissional] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const podeParticipar = useMemo(() => consulta?.esta_disponivel !== false, [consulta?.esta_disponivel]);

  const setVoto = (questionIndex: number, value: string) => {
    setVotos((prev) => {
      const copy = [...prev];
      while (copy.length <= questionIndex) copy.push('');
      copy[questionIndex] = value;
      return copy;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !consulta) return;
    setErro(null);
    setFeedback(null);

    const hasAnyVote = votos.some((v) => v);

    const payload = {
      token,
      pagina: pagina ? Number(pagina) : null,
      comentario: comentario.trim(),
      votos: votos,
      nome_completo: nome.trim(),
      cidade: cidade.trim(),
      estado: estado.trim().toUpperCase(),
      contato_email: email.trim(),
      area_atuacao_profissional: areaAtuacaoProfissional.trim(),
    };

    if (!payload.comentario && !hasAnyVote) {
      setErro('Escreva um comentário ou escolha uma opção de voto.');
      return;
    }

    try {
      await enviarManifestacao.mutateAsync(payload);
      setFeedback('Contribuição registrada! Obrigado por participar.');
      setComentario('');
      setVotos([]);
      setPagina('');
      setNome('');
      setCidade('');
      setEstado('');
      setEmail('');
      setAreaAtuacaoProfissional('');
    } catch (submitError: any) {
      setErro(submitError?.message ?? 'Não foi possível enviar a manifestação.');
    }
  };

  if (isLoading && !consulta) {
    return <FullPageLoader message="Carregando consulta pública..." />;
  }

  if (error) {
    return (
      <div className="consulta-publica">
        <div className="consulta-publica__card">
          <h1>Consulta pública</h1>
          <p>Não encontramos esta consulta ou o período já foi encerrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="consulta-publica">
      {consulta?.imagem_hero_url ? (
        <>
          <header className="consulta-publica__hero has-image">
            <img
              src={consulta.imagem_hero_url}
              alt={consulta.titulo}
              className="consulta-publica__hero-banner-img"
            />
          </header>
          <div className="consulta-publica__hero-info">
            <span className="consulta-publica__chip neutral">Consulta pública</span>
            <h1>{consulta.titulo}</h1>
            <p>{consulta.descricao || 'Documento disponível para leitura, comentários por página e voto com identificação.'}</p>
            <div className="consulta-publica__meta">
              <span>Publicação: {formatDate(consulta.data_publicacao)}</span>
              <span>Validade: {formatDate(consulta.data_validade)}</span>
              <span>Encerramento: {formatDate(consulta.data_fechamento)}</span>
            </div>
          </div>
        </>
      ) : (
        <header className="consulta-publica__hero">
          <div>
            <span className="consulta-publica__chip">Consulta pública</span>
            <h1>{consulta?.titulo ?? 'Consulta'}</h1>
            <p>{consulta?.descricao || 'Documento disponível para leitura, comentários por página e voto com identificação.'}</p>
            <div className="consulta-publica__meta">
              <span>Publicação: {formatDate(consulta?.data_publicacao)}</span>
              <span>Validade: {formatDate(consulta?.data_validade)}</span>
              <span>Encerramento: {formatDate(consulta?.data_fechamento)}</span>
            </div>
          </div>
        </header>
      )}

      <div className="consulta-publica__content">
        <section className="consulta-publica__panel pdf">
          <div className="consulta-publica__panel-header">
            <div>
              <h2>Documento em PDF</h2>
              <p>Use o visualizador para navegar por páginas antes de comentar.</p>
            </div>
          </div>
          {consulta?.pdf_url ? (
            <iframe src={consulta.pdf_url} title="PDF da consulta" className="consulta-publica__pdf" />
          ) : (
            <div className="consulta-publica__empty">PDF não encontrado.</div>
          )}
        </section>

        <section className="consulta-publica__panel">
          <div className="consulta-publica__panel-header">
            <div>
              <h2>Manifestar opinião</h2>
              <p>Deixe um comentário geral ou indique a página específica. Vote se desejar.</p>
            </div>
            {!podeParticipar && <span className="consulta-publica__chip warning">Encerrada</span>}
          </div>

          {feedback && <div className="consulta-publica__alert success">{feedback}</div>}
          {erro && <div className="consulta-publica__alert danger">{erro}</div>}

          <form className="consulta-publica__form" onSubmit={handleSubmit}>
            <label>
              <span>Página do PDF (opcional)</span>
              <input
                type="number"
                min={1}
                value={pagina}
                onChange={(event) => setPagina(event.target.value)}
                placeholder="Ex.: 3"
              />
            </label>
            <label className="full">
              <span>Comentário</span>
              <textarea
                rows={4}
                value={comentario}
                onChange={(event) => setComentario(event.target.value)}
                placeholder="Descreva sua sugestão ou apontamento sobre esta página ou o documento geral"
              />
            </label>

            {consulta?.perguntas_votacao && consulta.perguntas_votacao.length > 0 && (
              <div className="consulta-publica__polls full">
                {consulta.perguntas_votacao.map((pv, idx) => (
                  <div key={idx} className="consulta-publica__poll">
                    <strong>{pv.pergunta}</strong>
                    <div className="consulta-publica__options">
                      {(pv.opcoes || []).map((opcao) => (
                        <label key={opcao} className={(votos[idx] || '') === opcao ? 'is-selected' : ''}>
                          <input
                            type="radio"
                            name={`voto_${idx}`}
                            value={opcao}
                            checked={(votos[idx] || '') === opcao}
                            onChange={() => setVoto(idx, opcao)}
                          />
                          <span>{opcao}</span>
                        </label>
                      ))}
                      <label className={!(votos[idx] || '') ? 'is-selected' : ''}>
                        <input
                          type="radio"
                          name={`voto_${idx}`}
                          value=""
                          checked={!(votos[idx] || '')}
                          onChange={() => setVoto(idx, '')}
                        />
                        <span>Sem voto</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="consulta-publica__identificacao full">
              <h3>Identificação para registrar a consulta</h3>
              <p>Esses dados são necessários para validar a contribuição.</p>
              <div className="consulta-publica__identificacao-grid">
                <label>
                  <span>Nome completo</span>
                  <input type="text" required value={nome} onChange={(event) => setNome(event.target.value)} />
                </label>
                <label>
                  <span>Cidade</span>
                  <input type="text" required value={cidade} onChange={(event) => setCidade(event.target.value)} />
                </label>
                <label>
                  <span>Estado (UF)</span>
                  <input
                    type="text"
                    required
                    maxLength={2}
                    value={estado}
                    onChange={(event) => setEstado(event.target.value.toUpperCase())}
                  />
                </label>
                <label>
                  <span>E-mail</span>
                  <input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
                </label>
                <label>
                  <span>Área de atuação profissional</span>
                  <input
                    type="text"
                    required
                    value={areaAtuacaoProfissional}
                    onChange={(event) => setAreaAtuacaoProfissional(event.target.value)}
                  />
                </label>
              </div>
            </div>

            <button type="submit" disabled={enviarManifestacao.isPending || !podeParticipar}>
              {enviarManifestacao.isPending ? 'Enviando...' : 'Registrar manifestação'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
